import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

const SessionIdSchema = z.string().trim().min(4).max(80);

const BootstrapSchema = z.object({
  session_id: SessionIdSchema,
  favorite_team: z.string().trim().max(40).optional(),
  country: z.string().trim().max(8).optional(),
  city: z.string().trim().max(80).optional(),
});

export const bootstrapFanSession = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => BootstrapSchema.parse(input))
  .handler(async ({ data }) => {
    const now = new Date().toISOString();
    const { error: profileErr } = await supabaseAdmin.from("user_profiles").upsert(
      {
        session_id: data.session_id,
        favorite_team: data.favorite_team ?? null,
        country: data.country ?? "US",
        city: data.city ?? "Miami",
        last_seen_at: now,
      },
      { onConflict: "session_id" },
    );
    if (profileErr) console.warn("[fanProfile] user_profiles upsert failed", profileErr);
    const { error: insightErr } = await supabaseAdmin
      .from("fan_insights")
      .upsert({ session_id: data.session_id }, { onConflict: "session_id", ignoreDuplicates: true });
    if (insightErr) console.warn("[fanProfile] fan_insights upsert failed", insightErr);
    return { ok: true as const };
  });

const InsightField = z.enum([
  "predictions_made",
  "chat_messages_sent",
  "goal_celebrations_seen",
  "audio_sync_count",
]);

const BumpSchema = z.object({
  session_id: SessionIdSchema,
  field: InsightField,
  by: z.number().int().min(1).max(50).optional(),
});

export const bumpFanInsight = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => BumpSchema.parse(input))
  .handler(async ({ data }) => {
    const { data: row } = await supabaseAdmin
      .from("fan_insights")
      .select(data.field)
      .eq("session_id", data.session_id)
      .maybeSingle();
    const current = ((row as Record<string, number> | null)?.[data.field] ?? 0) as number;
    const next = current + (data.by ?? 1);
    const payload: Record<string, unknown> = {
      session_id: data.session_id,
      last_event_at: new Date().toISOString(),
      [data.field]: next,
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await supabaseAdmin.from("fan_insights").upsert(payload as any, { onConflict: "session_id" });
    if (error) {
      console.warn("[fanProfile] bumpInsight failed", error);
      return { ok: false as const, error: error.message };
    }
    return { ok: true as const, value: next };
  });

const SurveyInsightsSchema = z.object({
  session_id: SessionIdSchema,
  food_preference: z.string().trim().max(80).nullable().optional(),
  drink_preference: z.string().trim().max(80).nullable().optional(),
  favorite_brand: z.string().trim().max(80).nullable().optional(),
  purchase_intent: z.enum(["low", "medium", "high"]).optional(),
});

export const upsertSurveyInsights = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => SurveyInsightsSchema.parse(input))
  .handler(async ({ data }) => {
    const { error } = await supabaseAdmin.from("fan_insights").upsert(
      {
        session_id: data.session_id,
        food_preference: data.food_preference ?? null,
        drink_preference: data.drink_preference ?? null,
        favorite_brand: data.favorite_brand ?? null,
        purchase_intent: data.purchase_intent ?? "medium",
        last_event_at: new Date().toISOString(),
      },
      { onConflict: "session_id" },
    );
    if (error) return { ok: false as const, error: error.message };
    return { ok: true as const };
  });